import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, CircularProgress, TextField, Autocomplete,
  Grid, Card, LinearProgress, Stack, Chip, Divider, Paper,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PRIMARY } from 'themes/colors';
import { IRefillTransactionItem, IRefillTransactionAPIResponse } from 'definitions/app/core';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { fetchAgentsAsync, selectAgents } from '../../app/services/agents/agentSlice';
import { AppDispatch } from '../../app/store';
import { fetchRefillTransactionsByAgent } from '../../app/services/refillSale/refillSaleAPI';

interface AgentOption {
  label: string;
  value: string;
  refillCount?: number;
}

const calculatePercentage = (value: number, total: number) => {
  if (total === 0) return 0;
  return (value / total) * 100;
};

export default function RefillSalesSummary() {
  const [loading, setLoading] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AgentOption | null>(null);
  const [aggregatedItems, setAggregatedItems] = useState<IRefillTransactionItem[]>([]);
  const [totalTillDate, setTotalTillDate] = useState(0);
  const [totalDueAmount, setTotalDueAmount] = useState(0);
  const [totalFare, setTotalFare] = useState(0);

  const dispatch = useDispatch<AppDispatch>();
  const { agents: agentList, status: agentStatus } = useSelector(selectAgents);
  const hasFetchedAgents = useRef(false);

  useEffect(() => {
    if (agentStatus === 'idle' && !hasFetchedAgents.current) {
      dispatch(fetchAgentsAsync());
      hasFetchedAgents.current = true;
    }
  }, [dispatch, agentStatus]);

  const agentOptions: AgentOption[] = agentList.map(agent => ({
    label: agent.name,
    value: agent.name,
    refillCount: agent.refillCount,
  }));

  const [refillData, setRefillData] = useState<IRefillTransactionAPIResponse | null>(null);
  const [isRefillLoading, setIsRefillLoading] = useState(false);
  const [refillError, setRefillError] = useState<any>(null);

  useEffect(() => {
    const getRefillTransactions = async () => {
      if (selectedAgent?.value) {
        setIsRefillLoading(true);
        setRefillError(null);
        try {
          const response = await fetchRefillTransactionsByAgent(selectedAgent.value);
          setRefillData(response);
        } catch (err) {
          setRefillError(err);
        } finally {
          setIsRefillLoading(false);
        }
      } else {
        setRefillData(null);
        setAggregatedItems([]);
        setTotalTillDate(0);
        setTotalDueAmount(0);
        setTotalFare(0);
      }
    };
    getRefillTransactions();
  }, [selectedAgent]);

  useEffect(() => {
    setLoading(isRefillLoading);
    if (refillData?.result?.status === 'success' && refillData.result.data) {
      setAggregatedItems(refillData.result.data);
      setTotalTillDate(refillData.result.data.reduce((s: number, i: IRefillTransactionItem) => s + i.totalAmt, 0));
      setTotalDueAmount(refillData.result.data.reduce((s: number, i: IRefillTransactionItem) => s + i.dueAmt, 0));
      setTotalFare(refillData.result.data.reduce((s: number, i: IRefillTransactionItem) => s + (i.totalFare || 0), 0));
    } else {
      setAggregatedItems([]);
      setTotalTillDate(0);
      setTotalDueAmount(0);
      setTotalFare(0);
    }
    if (refillError) {
      console.error('Failed to fetch refill transactions:', refillError);
    }
  }, [refillData, isRefillLoading, refillError, selectedAgent]);

  const paidPct = calculatePercentage(totalTillDate - totalDueAmount, totalTillDate);

  return (
    <Box>
      {/* Agent Selector */}
      <Paper
        elevation={0}
        sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'grey.200' }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1.5, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}
        >
          Select Agent
        </Typography>
        <Autocomplete
          options={agentOptions}
          getOptionLabel={(option) => option.label}
          value={selectedAgent}
          onChange={(_, newValue: AgentOption | null) => setSelectedAgent(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Agent Name"
              placeholder="Search agent..."
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          )}
          sx={{ maxWidth: { xs: '100%', sm: 400 } }}
        />
      </Paper>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
          <CircularProgress size={48} thickness={4} />
          <Typography color="text.secondary">Loading refill data...</Typography>
        </Box>
      )}

      {/* Empty state */}
      {!loading && !selectedAgent && (
        <Paper
          elevation={0}
          sx={{
            p: 5, borderRadius: 3, textAlign: 'center',
            border: '1px dashed', borderColor: 'grey.300',
            bgcolor: alpha(PRIMARY.main, 0.02),
          }}
        >
          <LocalGasStationIcon sx={{ fontSize: 52, color: 'grey.300', mb: 1 }} />
          <Typography color="text.secondary" fontWeight={500}>
            Select an agent to view refill sale summary
          </Typography>
        </Paper>
      )}

      {/* Data */}
      {!loading && selectedAgent && aggregatedItems.length > 0 && (
        <Grid container spacing={2.5}>
          {/* Overall Summary Hero Card */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                background: `linear-gradient(135deg, ${PRIMARY.dark} 0%, ${PRIMARY.main} 100%)`,
                color: 'white',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <Box sx={{ position: 'absolute', top: -40, right: -40, width: 180, height: 180, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
              <Box sx={{ p: { xs: 2.5, sm: 3 }, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <Box
                    sx={{
                      width: 40, height: 40, borderRadius: 2,
                      bgcolor: 'rgba(255,255,255,0.15)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <ReceiptLongIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>Refill Sales Summary</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.8 }}>{selectedAgent.label}</Typography>
                  </Box>
                </Box>

                <Grid container spacing={3} sx={{ mb: 2.5 }}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Amount + Fare</Typography>
                    <Typography variant="h4" fontWeight={700}>
                      ₹{(totalTillDate + totalFare).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Fare</Typography>
                    <Typography variant="h4" fontWeight={700}>₹{totalFare.toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ opacity: 0.75 }}>Total Due</Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ color: '#ffa726' }}>
                      ₹{(totalDueAmount + totalFare).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>

                <LinearProgress
                  variant="determinate"
                  value={Math.min(paidPct, 100)}
                  sx={{
                    height: 8, borderRadius: 4,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    '& .MuiLinearProgress-bar': { bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 4 },
                  }}
                />
                <Typography variant="caption" sx={{ opacity: 0.75, mt: 0.5, display: 'block' }}>
                  {Math.round(paidPct)}% collected
                </Typography>
              </Box>
            </Card>
          </Grid>

          {/* Per-Item Cards */}
          {aggregatedItems.map((itemData, index) => (
            <Grid item xs={12} sm={6} lg={4} key={index}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: '1px solid', borderColor: 'grey.200',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  '&:hover': { boxShadow: '0 8px 24px rgba(0,0,0,0.08)', transform: 'translateY(-2px)' },
                }}
              >
                {/* Card Header */}
                <Box
                  sx={{
                    px: 2.5, py: 2,
                    bgcolor: alpha(PRIMARY.main, 0.04),
                    borderBottom: '1px solid', borderColor: 'grey.100',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 32, height: 32, borderRadius: 1.5,
                        bgcolor: alpha(PRIMARY.main, 0.1),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <LocalGasStationIcon sx={{ color: PRIMARY.main, fontSize: 16 }} />
                    </Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {itemData._id.item || 'Overall Summary'}
                    </Typography>
                  </Box>
                  <Chip
                    label={`Qty: ${itemData.quantity}`}
                    size="small"
                    sx={{ bgcolor: alpha(PRIMARY.main, 0.08), color: PRIMARY.dark, fontWeight: 600, fontSize: 11 }}
                  />
                </Box>

                {/* Card Body */}
                <Box sx={{ p: 2.5 }}>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                      <Typography variant="body2" fontWeight={600}>₹{itemData.totalAmt.toLocaleString()}</Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Paid Cash</Typography>
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        ₹{itemData.paidCash.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Paid Online</Typography>
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        ₹{itemData.paidOnline.toLocaleString()}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Due Amount</Typography>
                      <Typography variant="body2" fontWeight={700} color="error.main">
                        ₹{itemData.dueAmt.toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Total Fare</Typography>
                      <Typography variant="body2" fontWeight={600} color="secondary.main">
                        ₹{(itemData.totalFare || 0).toLocaleString()}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* No data */}
      {!loading && selectedAgent && aggregatedItems.length === 0 && (
        <Paper
          elevation={0}
          sx={{ p: 4, borderRadius: 3, textAlign: 'center', border: '1px solid', borderColor: 'grey.200' }}
        >
          <Typography color="text.secondary">No refill data found for {selectedAgent.label}</Typography>
        </Paper>
      )}
    </Box>
  );
}
